<script setup lang="ts">
import { definePage } from "unplugin-vue-router/runtime";
import { useForm } from "vee-validate";
import { useI18n } from "vue-i18n";
import { object, string } from "yup";
import UsersService from "@/api/models/auth/users";
import { useSnackStore } from "@/stores/snackStore";
import { useFormSubmitFlowLock } from "@/composables/shared/useFormSubmitFlowLock";
import { useGenericMutation } from "@/composables/shared/mutations/useGenericMutation";

definePage({
  meta: {
    title: "models.auth.resetPassword.title",
    layout: "login",
    requiresAuth: false,
  },
});

const { t } = useI18n();
const snackStore = useSnackStore();

const validationSchema = object({
  email: string().required(),
});

const { handleSubmit } = useForm({
  validationSchema,
});

const { mutateAsync: performResetPassword, isPending } = useGenericMutation<void, string>({
  mutationFn: (email) => {
    // TODO: ends with 403: Authentication credentials were not provided.
    return UsersService.resetPassword(email);
  },
  options: {
    onSuccess: async (): Promise<void> => {
      snackStore.showSuccess(t("models.auth.resetPassword.success"));
    },
  },
});

const { runSubmitFlow, isSubmitFlowPending } = useFormSubmitFlowLock();

const submit = handleSubmit((values) => {
  void runSubmitFlow(async () => {
    await performResetPassword(values.email);
  });
});
</script>

<template>
  <div class="tw:!ml-auto tw:w-full tw:md:w-1/2 tw:h-full tw:flex tw:items-center">
    <v-form
      class="tw:w-full tw:md:w-2/3"
      :validation-schema="validationSchema"
      @submit.prevent="submit"
    >
      <div class="tw:font-bold tw:text-2xl tw:md:text-4xl tw:!mb-1">
        {{ t("models.auth.resetPassword.title") }}
      </div>
      <div class="tw:!mt-10">
        <text-field
          name="email"
          :label="t('generic.fields.yourEmail')"
        />
      </div>
      <div class="tw:flex tw:justify-between tw:items-center">
        <v-btn
          class="tw:!text-xs"
          rounded
          size="large"
          min-width="150"
          variant="flat"
          color="teal"
          type="submit"
          :loading="isSubmitFlowPending || isPending"
        >
          {{ t("models.auth.actions.resetPassword") }}
        </v-btn>
      </div>
    </v-form>
  </div>
</template>
